import { supabase } from '../lib/supabase';


/**
 * Alert Notification Service
 * Provides centralized alert creation and broadcasting for admin users
 */

class AlertNotificationService {
  /**
   * Create a new admin alert
   * @param {string} alertType - Type of alert (new_flagged_content, new_user_registration, etc.)
   * @param {string} title - Alert title
   * @param {string} message - Alert message
   * @param {string} severity - Alert severity (info, warning, critical)
   * @param {string} relatedEntityId - Optional UUID of related entity
   */
  async createAlert(alertType, title, message, severity = 'info', relatedEntityId = null) {
    try {
      const { data, error } = await supabase?.rpc('create_admin_alert', {
        p_alert_type: alertType,
        p_title: title,
        p_message: message,
        p_severity: severity,
        p_related_entity_id: relatedEntityId
      });

      if (error) throw error;
      return { success: true, alertId: data };
    } catch (error) {
      console.error('Error creating alert:', error);
      return { success: false, error };
    }
  }

  /**
   * Trigger alert when content is flagged
   */
  async alertContentFlagged(contentId, contentType, reason) {
    return await this.createAlert(
      'new_flagged_content',
      'New Content Flagged',
      `A ${contentType} has been flagged for ${reason}`,
      'warning',
      contentId
    );
  }

  /**
   * Trigger alert when new user registers
   */
  async alertNewUserRegistration(userId, userEmail) {
    return await this.createAlert(
      'new_user_registration',
      'New User Registered',
      `New user registered: ${userEmail}`,
      'info',
      userId
    );
  }

  /**
   * Trigger alert for suspicious activity
   */
  async alertSuspiciousActivity(userId, activityDescription) {
    return await this.createAlert(
      'suspicious_activity',
      'Suspicious Activity Detected',
      activityDescription,
      'warning',
      userId
    );
  }

  /**
   * Trigger alert for system errors
   */
  async alertSystemError(errorMessage) {
    return await this.createAlert(
      'system_error',
      'System Error Occurred',
      errorMessage,
      'critical',
      null
    );
  }

  /**
   * Trigger alert for high value transactions
   */
  async alertHighValueTransaction(transactionId, amount, transactionType) {
    return await this.createAlert(
      'high_value_transaction',
      'High Value Transaction',
      `${transactionType} of £${amount} received`,
      'info',
      transactionId
    );
  }

  /**
   * Trigger alert for storage warnings
   */
  async alertStorageWarning(usagePercentage) {
    return await this.createAlert(
      'storage_warning',
      'Storage Quota Warning',
      `Storage usage at ${usagePercentage}%`,
      'warning',
      null
    );
  }

  /**
   * Mark alert as read
   */
  async markAlertRead(alertId) {
    try {
      const { error } = await supabase?.rpc('mark_alert_read', {
        p_alert_id: alertId
      });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error marking alert as read:', error);
      return { success: false, error };
    }
  }

  /**
   * Mark all alerts as read
   */
  async markAllAlertsRead() {
    try {
      const { error } = await supabase?.rpc('mark_all_alerts_read');

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error marking all alerts as read:', error);
      return { success: false, error };
    }
  }

  /**
   * Get unread alert count
   */
  async getUnreadCount() {
    try {
      const { count, error } = await supabase
        ?.from('admin_alerts')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('is_read', false);

      if (error) throw error;
      return { success: true, count: count || 0 };
    } catch (error) {
      console.error('Error getting unread count:', error);
      return { success: false, count: 0, error };
    }
  }

  /**
   * Subscribe to real-time alerts
   * @param {function} callback - Callback function to handle new alerts
   */
  subscribeToAlerts(callback) {
    const subscription = supabase
      ?.channel('admin_alerts_channel')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'admin_alerts'
        },
        (payload) => {
          callback(payload?.new);
        }
      )
      ?.subscribe();

    return subscription;
  }

  /**
   * Unsubscribe from real-time alerts
   */
  unsubscribeFromAlerts(subscription) {
    if (subscription) {
      supabase?.removeChannel(subscription);
    }
  }
}

export const alertNotificationService = new AlertNotificationService();
export default alertNotificationService;